import { PageHeader } from "@/components/PageHeader";
import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { TrialEligibilityOverride, TrialEligibilityRecord } from "@a7y/types";
import { normalizeEmailForFraud } from "@a7y/types";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  Textarea,
} from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, ShieldCheck, Trash2, X } from "lucide-react";
import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";

const PAGE_SIZE = 20;

type StatusMessage = { type: "success" | "error"; text: string };

type LookupState =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "loaded"; record: TrialEligibilityRecord | null }
  | { state: "error"; message: string };

export function TrialEligibilityPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // ---------- list + search ----------
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["trial-eligibility-overrides", { page, search: debouncedSearch }],
    queryFn: () =>
      api.getTrialEligibilityOverrides({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
  });

  const entries: TrialEligibilityOverride[] = data?.data ?? [];
  const total = data?.total ?? 0;

  // ---------- grant dialog ----------
  const [isGrantOpen, setIsGrantOpen] = useState(false);
  const [grantEmailRaw, setGrantEmailRaw] = useState("");
  const [grantReason, setGrantReason] = useState("");

  const grantNormalized = useMemo(() => {
    const trimmed = grantEmailRaw.trim();
    if (trimmed === "") return "";
    try {
      return normalizeEmailForFraud(trimmed);
    } catch {
      return "";
    }
  }, [grantEmailRaw]);

  const grantMutation = useMutation({
    mutationFn: (body: { emailNormalized: string; reason: string }) =>
      api.grantTrialEligibilityOverride(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trial-eligibility-overrides"] });
      setIsGrantOpen(false);
      setGrantEmailRaw("");
      setGrantReason("");
      showStatus("success", "Override granted");
    },
    onError: (err: Error) =>
      showStatus("error", `Failed to grant override: ${err.message}`),
  });

  const handleGrantSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!grantNormalized || grantReason.trim() === "") return;
    grantMutation.mutate({
      emailNormalized: grantNormalized,
      reason: grantReason.trim(),
    });
  };

  // ---------- revoke ----------
  const revokeMutation = useMutation({
    mutationFn: (emailNormalized: string) =>
      api.revokeTrialEligibilityOverride(emailNormalized),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trial-eligibility-overrides"] });
      showStatus("success", "Override revoked");
    },
    onError: (err: Error) =>
      showStatus("error", `Failed to revoke override: ${err.message}`),
  });

  const handleRevoke = async (entry: TrialEligibilityOverride) => {
    const ok = await confirm({
      title: `Revoke override for ${entry.emailNormalized}?`,
      description:
        "After revoking, any new trial attempt from this address will be denied again.",
      confirmLabel: "Revoke",
      confirmColor: "red",
    });
    if (ok) revokeMutation.mutate(entry.emailNormalized);
  };

  // ---------- record lookup ----------
  const [lookupEmailRaw, setLookupEmailRaw] = useState("");
  const [lookup, setLookup] = useState<LookupState>({ state: "idle" });

  const lookupNormalized = useMemo(() => {
    const trimmed = lookupEmailRaw.trim();
    if (trimmed === "") return "";
    try {
      return normalizeEmailForFraud(trimmed);
    } catch {
      return "";
    }
  }, [lookupEmailRaw]);

  const handleLookup = async (e: FormEvent) => {
    e.preventDefault();
    if (!lookupNormalized) return;
    setLookup({ state: "loading" });
    try {
      const res = await api.lookupTrialEligibilityRecord(lookupNormalized);
      setLookup({ state: "loaded", record: res.record ?? null });
    } catch (err) {
      setLookup({
        state: "error",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    }
  };

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Trial Eligibility"
        subtitle="Review trial slots and manage admin-granted overrides"
      />

      {/* Record lookup panel */}
      <div className="mb-8 rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Record lookup
        </h2>
        <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Enter a raw email to see whether it has consumed a trial slot. We look
          up the record by its normalized form.
        </Text>
        <form onSubmit={handleLookup} className="mt-4 flex flex-wrap items-end gap-3">
          <Field className="min-w-[280px] flex-1">
            <Label>Email</Label>
            <Input
              type="email"
              value={lookupEmailRaw}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setLookupEmailRaw(e.target.value)
              }
              placeholder="user@example.com"
              autoComplete="off"
            />
          </Field>
          <div className="min-w-[220px]">
            <div className="text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
              Normalized form
            </div>
            <div className="mt-2 rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 font-mono text-sm text-zinc-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200">
              {lookupNormalized || <span className="text-zinc-400">—</span>}
            </div>
          </div>
          <Button
            type="submit"
            color="blue"
            disabled={!lookupNormalized || lookup.state === "loading"}
          >
            {lookup.state === "loading" ? "Looking up..." : "Look up"}
          </Button>
        </form>

        {lookup.state === "error" && (
          <div className="mt-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 dark:bg-red-900/20 dark:text-red-400">
            {lookup.message}
          </div>
        )}

        {lookup.state === "loaded" && lookup.record === null && (
          <div className="mt-4 rounded-lg bg-zinc-50 px-4 py-3 text-sm text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
            No trial record found for{" "}
            <span className="font-mono">{lookupNormalized}</span>. This email has
            not consumed a trial slot.
          </div>
        )}

        {lookup.state === "loaded" && lookup.record && (
          <RecordCard record={lookup.record} />
        )}
      </div>

      {/* Search + Grant button */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="relative w-[340px]">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            autoComplete="off"
            placeholder="Search by normalized email..."
            value={search}
            onChange={handleSearchChange}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-10 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
                setPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button color="blue" onClick={() => setIsGrantOpen(true)}>
          Grant override
        </Button>
      </div>

      {/* Status banner */}
      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {/* Overrides table */}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>Normalized Email</TableHeader>
            <TableHeader>Reason</TableHeader>
            <TableHeader>Granted By</TableHeader>
            <TableHeader>Granted At</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
              </TableCell>
            </TableRow>
          ) : entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-8 text-center">
                <ShieldCheck className="mx-auto h-12 w-12 text-zinc-400" />
                <p className="mt-2 text-zinc-500">
                  {debouncedSearch
                    ? "No overrides match your search"
                    : "No trial-eligibility overrides"}
                </p>
              </TableCell>
            </TableRow>
          ) : (
            entries.map((entry) => {
              const revoked = Boolean(entry.revokedAt);
              return (
                <TableRow key={entry.emailNormalized}>
                  <TableCell className="font-medium font-mono text-sm">
                    {entry.emailNormalized}
                  </TableCell>
                  <TableCell className="max-w-[360px] truncate text-sm">
                    {entry.reason}
                  </TableCell>
                  <TableCell className="text-sm">
                    {entry.grantedByEmail}
                  </TableCell>
                  <TableCell className="text-zinc-500 text-sm">
                    {formatDate(entry.grantedAt)}
                  </TableCell>
                  <TableCell>
                    {revoked ? (
                      <Badge color="zinc">Revoked</Badge>
                    ) : (
                      <Badge color="green">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {!revoked && (
                      <Button
                        plain
                        onClick={() => handleRevoke(entry)}
                        disabled={revokeMutation.isPending}
                        title="Revoke override"
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-between">
          <Text className="text-sm text-gray-700 dark:text-zinc-300">
            Showing{" "}
            <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(page * PAGE_SIZE, total)}
            </span>{" "}
            of <span className="font-medium">{total}</span> results
          </Text>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              outline
            >
              Previous
            </Button>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page * PAGE_SIZE >= total}
              outline
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Grant override dialog */}
      {isGrantOpen && (
        <Dialog open onClose={() => setIsGrantOpen(false)}>
          <DialogTitle>Grant trial-eligibility override</DialogTitle>
          <DialogBody>
            <Text className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
              Granting an override lets this email start a new trial even if a
              prior trial was consumed. The key we store is the{" "}
              <span className="font-mono">normalized</span> form — shown below
              before you submit.
            </Text>
            <form id="grant-override-form" onSubmit={handleGrantSubmit}>
              <div className="space-y-4">
                <Field>
                  <Label>Email (raw)</Label>
                  <Input
                    type="email"
                    value={grantEmailRaw}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setGrantEmailRaw(e.target.value)
                    }
                    placeholder="user@example.com"
                    autoComplete="off"
                    autoFocus
                  />
                </Field>
                <div>
                  <div className="text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
                    Normalized (will be stored)
                  </div>
                  <div className="mt-2 rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 font-mono text-sm text-zinc-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200">
                    {grantNormalized || <span className="text-zinc-400">—</span>}
                  </div>
                </div>
                <Field>
                  <Label>Reason</Label>
                  <Textarea
                    value={grantReason}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      setGrantReason(e.target.value)
                    }
                    placeholder="e.g. backfill collision: a.b vs ab; approved by founder in #trial-ops"
                    rows={3}
                  />
                </Field>
              </div>
            </form>
            {grantMutation.isError && (
              <Text className="mt-2 text-sm text-red-500">
                Failed: {(grantMutation.error as Error)?.message}
              </Text>
            )}
          </DialogBody>
          <DialogActions>
            <Button plain onClick={() => setIsGrantOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="grant-override-form"
              color="blue"
              disabled={
                !grantNormalized ||
                grantReason.trim() === "" ||
                grantMutation.isPending
              }
            >
              {grantMutation.isPending ? "Granting..." : "Grant override"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

function RecordCard({ record }: { record: TrialEligibilityRecord }) {
  const rows: Array<{ label: string; value: ReactNode }> = [
    { label: "Original email", value: <span className="font-mono">{record.emailOriginal}</span> },
    { label: "Normalized email", value: <span className="font-mono">{record.emailNormalized}</span> },
    { label: "Org ID", value: <span className="font-mono">{record.orgId}</span> },
    {
      label: "User ID",
      value: record.userId ? (
        <span className="font-mono">{record.userId}</span>
      ) : (
        <span className="text-zinc-400">—</span>
      ),
    },
    { label: "Trial started", value: formatDate(record.trialStartedAt) },
    {
      label: "Trial expired",
      value: record.trialExpiredAt ? (
        formatDate(record.trialExpiredAt)
      ) : (
        <Badge color="amber">Still active</Badge>
      ),
    },
  ];
  return (
    <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-col gap-0.5 text-sm">
            <dt className="text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              {row.label}
            </dt>
            <dd className="text-zinc-900 dark:text-zinc-100">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
